import { useEffect, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { TaskTemplatePicker } from 'app/components/task-templates/TaskTemplatePicker'
import { toEngineText } from 'app/lib/messageTemplates'
import type { TaskTemplate } from 'app/generated/graphql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import { bg } from 'lib/background'
import { DUE_TYPES, type Option } from 'app/lib/workflows/actionCatalogs'
import type { CreateTaskNodeData, DueType, EditTaskNodeData } from 'app/types/workflows'
import { fieldsToVariables } from 'app/lib/workflows/dataContext'
import { type Panel } from './panelShared'
import { DateInput } from './DateInput'
import { ChoiceSelect, type Choice, Field, MessageField, TextField, ToggleField, VariableTextField } from './fields'
import { Section } from './Section'
import { optionChoices, useUserOptions } from './panelShared'

const TASK_OWNER_TOKENS: Option[] = [
  { id: ``, name: `Unassigned` },
  { id: `contact-owner`, name: `Contact's owner` },
]

/** Workspace roles as task-owner options, carried as `role:<id>`. Undefined until loaded. */
function useRoleOptions(): Option[] | undefined {
  const [roles, setRoles] = useState<Option[]>()
  useEffect(() => {
    let alive = true
    bg(
      gqlFetchList<Option>(`roles`, { pagination: { page: 0, limit: 100 } }, `id name`).then(
        (result) =>
          alive && setRoles(result.hits.map((role) => ({ id: `${ROLE_ASSIGNEE_PREFIX}${role.id}`, name: role.name }))),
      ),
    )
    return () => {
      alive = false
    }
  }, [])
  return roles
}

export function CreateTaskPanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<CreateTaskNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  const people = useUserOptions()
  const roles = useRoleOptions()
  const owners = [...(roles ?? []), ...people]
  // Roles load async, so a role owner is only "gone" once the list has arrived without it.
  const staleRole =
    !!roles && !!data.ownerId?.startsWith(ROLE_ASSIGNEE_PREFIX) && !owners.some((o) => o.id === data.ownerId)
  const variables = fieldsToVariables(availableFields)
  return (
    <>
      <Section title="Task">
        <div className="mb-1 flex justify-end text-xs">
          <TaskTemplatePicker
            trigger={
              <Button variant="link" size="inline">
                Use template
              </Button>
            }
            onSelect={(t: TaskTemplate) => {
              const title = toEngineText(t.title)
              // Stale-owner check against the panel's own staff options; no
              // ownerName field exists on CreateTaskNodeData (checked types.ts).
              const owner = t.ownerId ? owners.find((o) => o.id === t.ownerId) : undefined
              // Fill-only semantics: fields the template doesn't define keep
              // their current node values (an explicit `description: undefined`
              // would clobber hand-typed text via updateNodeData's merge).
              set({
                title,
                ...(t.description ? { description: toEngineText(t.description) } : {}),
                ...(t.dueInDays != null ? { dueType: `relative` as const, dueInDays: t.dueInDays } : {}),
                ...(owner ? { ownerId: owner.id } : {}),
              })
            }}
          />
        </div>
        <Field label="Task title" error={errors.title}>
          <VariableTextField
            value={data.title}
            error={errors.title}
            onChange={(title) => set({ title })}
            variables={variables}
            sample={sample}
            placeholder="e.g. Call the new lead"
          />
        </Field>
        <Field label="Description">
          <MessageField
            value={data.description ?? ``}
            onChange={(description) => set({ description: description || undefined })}
            variables={variables}
            sample={sample}
            rows={3}
          />
        </Field>
      </Section>
      <Section title="Owner and due date">
        <Field label="Owner" error={staleRole ? `This role no longer exists. Pick another owner.` : undefined}>
          <ChoiceSelect
            value={data.ownerId ?? ``}
            invalid={staleRole}
            groups={[
              { label: `Assign to`, choices: optionChoices(TASK_OWNER_TOKENS) },
              { label: `Roles`, choices: optionChoices(roles ?? []) },
              { label: `People`, choices: optionChoices(people) },
            ]}
            onChange={(v) => set({ ownerId: v || undefined })}
          />
        </Field>
        <Field label="Due">
          <ChoiceSelect value={data.dueType} choices={DUE_TYPES} onChange={(v) => set({ dueType: v as DueType })} />
        </Field>
        {data.dueType === `relative` && (
          <Field label="Days after it runs" info="0 is due the same day it runs." error={errors.dueInDays}>
            <TextField
              type="number"
              min={0}
              invalid={!!errors.dueInDays}
              value={data.dueInDays ?? ``}
              onChange={(e) =>
                set({ dueInDays: e.target.value === `` ? undefined : Math.max(0, Number(e.target.value)) })
              }
            />
          </Field>
        )}
        {data.dueType === `absolute` && (
          <Field label="Due date" error={errors.dueDate}>
            <DateInput
              aria-label="Due date"
              invalid={!!errors.dueDate}
              value={data.dueDate}
              onChange={(day) => set({ dueDate: day || undefined })}
            />
          </Field>
        )}
      </Section>
    </>
  )
}

/** "Leave as is" for an Edit task select. A real value never collides with it. */
const KEEP = `keep`

const STATUS_CHANGES: Choice[] = [
  { value: KEEP, label: `Leave as is` },
  { value: `closed`, label: `Close it` },
  { value: `open`, label: `Reopen it` },
]

const ARCHIVE_CHANGES: Choice[] = [
  { value: KEEP, label: `Leave as is` },
  { value: `true`, label: `Archive it` },
  { value: `false`, label: `Unarchive it` },
]

export function EditTaskPanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<EditTaskNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  const people = useUserOptions()
  const roles = useRoleOptions()
  const variables = fieldsToVariables(availableFields)
  return (
    <>
      <Section title="Which task">
        <div className="text-xs text-subtle">The task made by the Create task this path starts from.</div>
        <ToggleField
          label="Only if it's still open"
          hint="Skips this step when someone already closed the task, so finished work is never archived or reopened."
          checked={data.onlyIfOpen}
          onChange={(onlyIfOpen) => set({ onlyIfOpen })}
        />
      </Section>
      <Section title="Changes">
        {errors.changes && <div className="text-[11px] text-red-600">{errors.changes}</div>}
        <Field label="Status">
          <ChoiceSelect
            value={data.status ?? KEEP}
            choices={STATUS_CHANGES}
            onChange={(v) => set({ status: v === KEEP ? undefined : v })}
          />
        </Field>
        <Field label="Archive" info="An archived task leaves the Tasks list without being marked done.">
          <ChoiceSelect
            value={data.archived === undefined ? KEEP : String(data.archived)}
            choices={ARCHIVE_CHANGES}
            onChange={(v) => set({ archived: v === KEEP ? undefined : v === `true` })}
          />
        </Field>
        <Field label="Owner">
          <ChoiceSelect
            value={data.ownerId ?? KEEP}
            groups={[
              {
                label: `Owner`,
                choices: [
                  { value: KEEP, label: `Leave as is` },
                  { value: ``, label: `Unassigned` },
                ],
              },
              { label: `Roles`, choices: optionChoices(roles ?? []) },
              { label: `People`, choices: optionChoices(people) },
            ]}
            onChange={(v) => set({ ownerId: v === KEEP ? undefined : v })}
          />
        </Field>
        <Field label="Due">
          <ChoiceSelect
            value={data.dueType ?? KEEP}
            choices={[{ value: KEEP, label: `Leave as is` }, ...DUE_TYPES]}
            onChange={(v) => set({ dueType: v === KEEP ? undefined : (v as DueType) })}
          />
        </Field>
        {data.dueType === `relative` && (
          <Field label="Days after it runs" info="0 is due the same day it runs." error={errors.dueInDays}>
            <TextField
              type="number"
              min={0}
              invalid={!!errors.dueInDays}
              value={data.dueInDays ?? ``}
              onChange={(e) =>
                set({ dueInDays: e.target.value === `` ? undefined : Math.max(0, Number(e.target.value)) })
              }
            />
          </Field>
        )}
        {data.dueType === `absolute` && (
          <Field label="Due date" error={errors.dueDate}>
            <DateInput
              aria-label="Due date"
              invalid={!!errors.dueDate}
              value={data.dueDate}
              onChange={(day) => set({ dueDate: day || undefined })}
            />
          </Field>
        )}
        <Field label="Title" hint="Leave blank to keep the current title.">
          <VariableTextField
            value={data.title ?? ``}
            onChange={(title) => set({ title: title || undefined })}
            variables={variables}
            sample={sample}
            placeholder="Leave as is"
          />
        </Field>
        <Field label="Description" hint="Leave blank to keep the current description.">
          <MessageField
            value={data.description ?? ``}
            onChange={(description) => set({ description: description || undefined })}
            variables={variables}
            sample={sample}
            rows={3}
          />
        </Field>
      </Section>
    </>
  )
}
